import { ButtonColor, FormActions } from '@doveit/lego';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { OptInType } from '../../../domain/optInType';
import { OptInV2, SourceType } from '../../../services/dtos';
import { JobApplication } from '../../../services/dtos/jobApplication';
import { saveJobApplication } from '../../../services/job-applications/jobApplications';
import JobApplicationForm, { JobApplicationFormModel } from '../../components/job-application-form/JobApplicationForm';
import getTrafficSource from '../../utils/traffic-source/getTrafficSource';
import getCookie from '../../utils/cookie/getCookie';

export interface SaveJobApplicationProps {
  ctaLabel?: string,
  onSuccess?: (application: JobApplication) => void,
  jobPositionId: string,
  showProvince?: boolean,
  source?: string,
  verticalOnly?: boolean,
  dataRef?: string,
  dataTracking?: string,
  submitColor?: ButtonColor,
}

export const SUCCESS_MESSAGE = 'La tua candidatura è stata inviata. Sarai contattato al più presto.';
export const ERROR_MESSAGE = 'Non è stato possibile inviare la candidatura. Riprova più tardi.';

const SaveJobApplication: FunctionComponent<SaveJobApplicationProps> = ({
  ctaLabel,
  onSuccess,
  jobPositionId,
  showProvince,
  source,
  verticalOnly,
  dataRef,
  dataTracking,
  submitColor = ButtonColor.SECONDARY,
}) => {
  const [isSaving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { query } = useRouter();

  const onSubmit = useCallback(
    async (values: JobApplicationFormModel, actions: FormActions<JobApplicationFormModel>) => {
      setSuccessMessage(undefined);
      setErrorMessage(undefined);
      setSaving(true);

      try {
        const optIns: OptInV2[] = [{
          type: OptInType.SERVICE,
          sourceType: SourceType.WEB,
          sourceValue: 'dove.it',
        }];

        if (values.marketing) {
          optIns.push({
            type: OptInType.MARKETING,
            sourceType: SourceType.WEB,
            sourceValue: 'dove.it',
          });
        }

        const savedApplication = await saveJobApplication({
          name: values.name,
          email: values.email,
          phoneNumber: values.phoneNumber,
          jobPositionId,
          message: values.message || undefined,
          province: values.province || undefined,
          source: source || getTrafficSource(query) as string,
          optIns,
          gclid: query.gclid as string || undefined,
          fbc: getCookie('_fbc'),
          fbp: getCookie('_fbp'),
          utmCampaign: query.utm_campaign as string || undefined,
          originUrl: window.location.href,
        });

        setSuccessMessage(SUCCESS_MESSAGE);
        actions.resetForm();

        if (onSuccess) {
          onSuccess(savedApplication);
        }
      } catch (error) {
        setErrorMessage(ERROR_MESSAGE);
      }
      setSaving(false);
    }, [jobPositionId, source, query, onSuccess],
  );
  return (
    <JobApplicationForm
      ctaLabel={ctaLabel}
      onSubmit={onSubmit}
      successMessage={successMessage}
      errorMessage={errorMessage}
      loading={isSaving}
      showProvince={showProvince}
      dataRef={dataRef}
      dataTracking={dataTracking}
      verticalOnly={verticalOnly}
      submitColor={submitColor}
    />
  );
};

export default SaveJobApplication;
